import { useEffect, useState } from 'react';

export type SortOrder = 'asc' | 'desc';

type UseSortConfig = {
    order: SortOrder;
    defaultOption?: string;
    enableV2Query?: boolean;
};

export type SortInstance = {
    updateSortOrder: (order: SortOrder) => void;
    updateSortOption: (option: string) => void;
    reset: () => void;
    clear: () => void;
    apply: () => void;
    sortOrder: string;
    sortOption?: string;
    result: {
        queryString: string;
        currentSortState: {
            order: SortOrder;
            option?: string;
        };
    };
};

type SortPreference = {
    prev: { order: SortOrder; option?: string };
    current: { order: SortOrder; option?: string };
    queryString: string;
};

export function useSort({ order, defaultOption, enableV2Query = false }: UseSortConfig): SortInstance {
    const [sortPreference, setSortPreference] = useState<SortPreference>({
        prev: { order, option: defaultOption },
        current: { order, option: defaultOption },
        queryString: buildActiveSortQueryString(order, defaultOption),
    });

    useEffect(() => {
        setSortPreference((prevPreferences) => {
            return { ...prevPreferences, queryString: buildActiveSortQueryString(prevPreferences.prev.order, prevPreferences.prev.option) };
        });
    }, [sortPreference.prev]);

    function updateSortOrder(order: SortOrder): void {
        setSortPreference((prevPreference) => {
            return { ...prevPreference, current: { ...prevPreference.current, order } };
        });
    }

    function clear(): void {
        setSortPreference((prevPreferences) => {
            return { ...prevPreferences, prev: { order: 'asc', option: undefined }, current: { order: 'asc', option: undefined } };
        });
    }

    function reset(): void {
        setSortPreference((prevPreference) => {
            return { ...prevPreference, current: { ...prevPreference.prev } };
        });
    }

    function apply(): void {
        setSortPreference((prevPreference) => {
            return { ...prevPreference, prev: { ...prevPreference.current } };
        });
    }

    function updateSortOption(option: string): void {
        setSortPreference((prevPreference) => {
            return { ...prevPreference, current: { ...prevPreference.current, option } };
        });
    }

    function buildQueryString(order: SortOrder, option?: string): string {
        if (!option) {
            return '';
        }

        const qs = new URLSearchParams();

        qs.append('sort[dir]', order);
        qs.append('sort[field]', option);

        return qs.toString();
    }

    function buildActiveSortQueryString(order: SortOrder, option?: string): string {
        if (enableV2Query) {
            return buildQueryString(order, option);
        }

        if (!option) {
            return '';
        }

        const search = encodeURIComponent(JSON.stringify({ order, option }));
        return search;
    }

    return {
        updateSortOrder,
        updateSortOption,
        reset,
        clear,
        apply,
        sortOrder: sortPreference.current.order,
        sortOption: sortPreference.current.option,
        result: { queryString: sortPreference.queryString, currentSortState: sortPreference.current },
    };
}
